import type { RenderPort } from '../ports/render-port';
import { Palette } from './palette';
import type { PaletteColour } from './palette';
import { FIELD_HEIGHT, FIELD_WIDTH } from './screen';

interface StarLayer {
  readonly count: number;
  /** Scroll speed in play-field pixels per second. */
  readonly speed: number;
  readonly size: number;
  readonly colour: PaletteColour;
}

/** Far to near: nearer layers are faster, bigger and brighter (parallax). */
export const STARFIELD_LAYERS: readonly StarLayer[] = [
  { count: 40, speed: 12, size: 1, colour: Palette.starFar },
  { count: 24, speed: 30, size: 1, colour: Palette.starMid },
  { count: 10, speed: 70, size: 2, colour: Palette.starNear },
];

/**
 * Scrolling background of the play field. Star positions are a pure function of the scroll time
 * (ADR-0010): the table is drawn once from the injected, unseeded presentation randomness, so the
 * gameplay `Random` is never consumed and drawing allocates nothing.
 */
export class Starfield {
  private readonly xs: Float32Array;
  private readonly ys: Float32Array;

  constructor(random: () => number) {
    let total = 0;
    for (let l = 0; l < STARFIELD_LAYERS.length; l += 1) total += STARFIELD_LAYERS[l]?.count ?? 0;
    this.xs = new Float32Array(total);
    this.ys = new Float32Array(total);
    let i = 0;
    for (let l = 0; l < STARFIELD_LAYERS.length; l += 1) {
      const layer = STARFIELD_LAYERS[l];
      if (layer === undefined) continue;
      for (let n = 0; n < layer.count; n += 1) {
        this.xs[i] = Math.floor(random() * (FIELD_WIDTH - layer.size));
        this.ys[i] = random() * FIELD_HEIGHT;
        i += 1;
      }
    }
  }

  /** Draws every layer in the current region, which the caller sets to the play field. */
  draw(render: RenderPort, timeSeconds: number): void {
    this.drawLayers(render, timeSeconds, 0, STARFIELD_LAYERS.length);
  }

  /**
   * Draws the layers `from` (included) to `to` (excluded), far to near, so something can sit
   * between two layers (the champion's name, GDD v0.22 §9.4.1).
   */
  drawLayers(render: RenderPort, timeSeconds: number, from: number, to: number): void {
    let i = 0;
    for (let l = 0; l < to; l += 1) {
      const layer = STARFIELD_LAYERS[l];
      if (layer === undefined) continue;
      if (l < from) {
        i += layer.count;
        continue;
      }
      const offset = layer.speed * timeSeconds;
      for (let n = 0; n < layer.count; n += 1) {
        const y = ((((this.ys[i] ?? 0) + offset) % FIELD_HEIGHT) + FIELD_HEIGHT) % FIELD_HEIGHT;
        render.drawRect(this.xs[i] ?? 0, y, layer.size, layer.size, layer.colour);
        i += 1;
      }
    }
  }
}
